import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { trigger, transition, style, animate, query, stagger } from '@angular/animations';
import { MiniGameService } from '../../../_services/mini-game.service';
import { StudentScoreEntry } from '../../../interfaces/mini-game.interface';
import { GlobalService } from 'src/app/_services/global.service';

@Component({
  selector: 'app-live-leaderboard',
  templateUrl: './live-leaderboard.component.html',
  styleUrls: ['./live-leaderboard.component.scss'],
  animations: [
    trigger('listEntry', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(40px)' }),
        animate('0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ]),
    trigger('xpPop', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0) translateY(10px)' }),
        animate('0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          style({ opacity: 1, transform: 'scale(1) translateY(0)' }))
      ]),
      transition(':leave', [
        animate('0.3s ease-out',
          style({ opacity: 0, transform: 'translateY(-20px)' }))
      ])
    ]),
    trigger('peekSlide', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-12px) scale(0.92)' }),
        animate('0.35s 0.1s cubic-bezier(0.34, 1.56, 0.64, 1)',
          style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
      ]),
      transition(':leave', [
        animate('0.2s ease-in',
          style({ opacity: 0, transform: 'translateY(-8px) scale(0.95)' }))
      ])
    ]),
    trigger('peekRow', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(16px)' }),
        animate('0.3s 0.15s cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ])
  ]
})
export class LiveLeaderboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  scores: StudentScoreEntry[] = [];
  currentStudentId: number | null = null;
  isCollapsed = true;
  showXpFloat: { [studentId: number]: number | null } = {};

  private xpFloatTimers: { [studentId: number]: any } = {};

  constructor(
    private miniGameService: MiniGameService,
    private globalService: GlobalService
  ) {}

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.studentScores$
      .pipe(takeUntil(this.destroy$))
      .subscribe(scores => {
        // Trigger XP float animation for changed scores
        scores.forEach(s => {
          if (s.lastXpGain > 0) {
            this.triggerXpFloat(s.student.id, s.lastXpGain);
          }
        });
        this.scores = scores;
      });

    this.miniGameService.currentStudentId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(id => this.currentStudentId = id);

    // Sync initial state
    this.miniGameService.setLeaderboardOpen(!this.isCollapsed);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    Object.values(this.xpFloatTimers).forEach(t => clearTimeout(t));
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.miniGameService.setLeaderboardOpen(!this.isCollapsed);
  }

  get topStudents(): StudentScoreEntry[] {
    return this.scores.filter(s => s.rank <= 3);
  }

  getStudentName(entry: StudentScoreEntry): string {
    return this.currLang === 'ar' ? entry.student.name.ar : entry.student.name.en;
  }

  getShortName(entry: StudentScoreEntry): string {
    const name = this.getStudentName(entry);
    const parts = name.split(' ');
    if (parts.length > 1) {
      return parts[0] + ' ' + parts[1].charAt(0) + '.';
    }
    return parts[0];
  }

  getRankIcon(rank: number): string {
    switch (rank) {
      case 1: return '\uD83D\uDC51'; // crown
      case 2: return '\uD83E\uDD48'; // silver medal
      case 3: return '\uD83E\uDD49'; // bronze medal
      default: return '';
    }
  }

  getRankChangeIcon(change: string): string {
    switch (change) {
      case 'up': return '\u25B2';     // up triangle
      case 'down': return '\u25BC';   // down triangle
      default: return '';
    }
  }

  getStreakEmoji(streak: number): string {
    if (streak >= 5) return '\uD83D\uDD25'; // fire
    if (streak >= 3) return '\u26A1';       // lightning
    return '';
  }

  getRowTransform(index: number): string {
    return `translateY(${index * 52}px)`;
  }

  trackByStudent(index: number, entry: StudentScoreEntry): number {
    return entry.student.id;
  }

  private triggerXpFloat(studentId: number, xp: number): void {
    if (this.xpFloatTimers[studentId]) {
      clearTimeout(this.xpFloatTimers[studentId]);
    }
    this.showXpFloat[studentId] = xp;
    this.xpFloatTimers[studentId] = setTimeout(() => {
      this.showXpFloat[studentId] = null;
    }, 1200);
  }
}
